import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, MapPin, Star } from "lucide-react";
import { SmartImage } from "./SmartImage";
import { VERIFIED, LOCATIONS } from "@/data/cafe";
import { useDialogs } from "./DialogProvider";
import { scrollToId } from "@/lib/scroll";

const ease = [0.33, 1, 0.68, 1];

const MaskWord = ({ children, delay }) => (
  <span className="block overflow-hidden pb-[0.08em]">
    <motion.span className="block" initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 1, ease, delay }}>
      {children}
    </motion.span>
  </span>
);

const KULHAD = "https://images.unsplash.com/photo-1630748662359-40a2105640c7?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000";
const INTERIOR = VERIFIED.interior2;

export default function Hero() {
  const { open } = useDialogs();
  const scrollTo = (id) => scrollToId(id);

  return (
    <section className="relative min-h-[100svh] w-full overflow-hidden bg-[#F7F5EF]" data-testid="hero-section">
      {/* Ambient glow */}
      <motion.div aria-hidden className="absolute -top-24 -right-24 h-[22rem] sm:h-[26rem] w-[22rem] sm:w-[26rem] rounded-full bg-[#1C7C54]/12 blur-[90px] pointer-events-none"
        animate={{ x: [0, -40, 0], y: [0, 40, 0] }} transition={{ duration: 16, repeat: Infinity, ease: "easeInOut" }} />
      <motion.div aria-hidden className="absolute bottom-10 left-10 h-56 w-56 rounded-full bg-[#C29A6B]/15 blur-[80px] pointer-events-none"
        animate={{ x: [0, 40, 0], y: [0, -30, 0] }} transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }} />

      <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-8 pt-24 md:pt-32 pb-14 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
        {/* Left text */}
        <div className="lg:col-span-6">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="flex items-center gap-3">
            <span className="h-[2px] w-8 bg-[#1C7C54]" />
            <span className="overline text-[#1C7C54]">Teaavi Coffee — Delhi NCR · Since 2019</span>
          </motion.div>

          <h1 className="font-display font-medium tracking-tight text-[#1A1514] text-[2.75rem] sm:text-6xl lg:text-[6rem] leading-[0.98] mt-4">
            <MaskWord delay={0.25}>Fresh Coffee.</MaskWord>
            <MaskWord delay={0.4}><span className="italic text-[#1C7C54]">Fresh Moments.</span></MaskWord>
          </h1>

          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.7, ease }}
            className="font-body text-[#1A1514]/70 text-[15px] sm:text-lg max-w-md mt-5 leading-relaxed">
            Handcrafted coffee, chai in kulhads, warm meals and a cozy corner to call your own — brewed with passion across Delhi NCR.
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85, duration: 0.6, ease }}
            className="flex items-center gap-4 mt-6" data-testid="hero-rating">
            <div className="flex">{[...Array(5)].map((_, i) => <Star key={i} className="h-4 w-4 fill-[#1C7C54] text-[#1C7C54]" />)}</div>
            <span className="font-body text-sm text-[#1A1514]/70">4.9 / 5 · Loved by 12,000+ regulars</span>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.95, duration: 0.6, ease }}
            className="flex flex-wrap gap-2 mt-5" data-testid="hero-locations">
            {LOCATIONS.map((l) => (
              <button key={l.name} onClick={() => scrollTo("#locations")}
                className="inline-flex items-center gap-1.5 rounded-full border border-[#1A1514]/15 bg-white/70 backdrop-blur px-3 py-1.5 font-accent text-[10.5px] uppercase tracking-[0.12em] text-[#1A1514] hover:border-[#1C7C54] hover:text-[#1C7C54] transition-colors">
                <MapPin className="h-3 w-3 text-[#1C7C54]" />{l.name.replace("Teaavi Coffee — ", "")}
              </button>
            ))}
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05, duration: 0.7, ease }}
            className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2.5 sm:gap-3 mt-7">
            <button onClick={() => scrollTo("#menu")} data-testid="hero-view-menu"
              className="col-span-2 sm:col-span-1 group inline-flex items-center justify-center gap-2 rounded-sm bg-[#1C7C54] text-white px-6 sm:px-7 py-3.5 font-accent text-[11px] uppercase tracking-[0.15em] hover:bg-[#14603F] transition-colors">
              View Menu <ArrowRight className="h-4 w-4 -translate-x-1 opacity-0 group-hover:translate-x-0 group-hover:opacity-100 transition-all duration-300" />
            </button>
            <button onClick={() => open("order")} data-testid="hero-order-online"
              className="rounded-sm border border-[#1A1514]/25 text-[#1A1514] px-6 sm:px-7 py-3.5 font-accent text-[11px] uppercase tracking-[0.15em] hover:bg-[#1A1514]/5 transition-colors">
              Order Online
            </button>
            <button onClick={() => open("reservation")} data-testid="hero-reserve"
              className="rounded-sm border border-[#1A1514]/25 text-[#1A1514] px-6 sm:px-7 py-3.5 font-accent text-[11px] uppercase tracking-[0.15em] hover:bg-[#1A1514]/5 transition-colors">
              Reserve a Table
            </button>
          </motion.div>
        </div>

        {/* Right — mood board collage */}
        <div className="lg:col-span-6 relative">
          <div className="grid grid-cols-6 grid-rows-6 gap-3 sm:gap-4 h-[64vh] sm:h-[70vh] lg:h-[76vh]" data-testid="hero-collage">
            {/* Main tall image */}
            <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.9, ease }}
              className="col-span-4 row-span-6 relative overflow-hidden rounded-sm shadow-[0_25px_50px_-25px_rgba(26,21,20,0.5)]" data-testid="hero-img-main">
              <motion.div animate={{ y: [0, -10, 0] }} transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }} className="h-full w-full">
                <SmartImage src={VERIFIED.hero} alt="Teaavi Coffee café" className="h-full w-full object-cover" />
              </motion.div>
              <div className="absolute inset-0 bg-gradient-to-t from-[#1A1514]/25 to-transparent" />
              <span className="absolute top-4 left-4 bg-white/95 backdrop-blur px-3 py-1 rounded-full font-accent text-[10px] uppercase tracking-[0.18em] text-[#1C7C54]">Signature</span>
            </motion.div>

            {/* Small top image — kulhad chai */}
            <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.5, duration: 0.9, ease }}
              className="col-span-2 row-span-3 relative overflow-hidden rounded-sm rotate-[2deg] hover:rotate-0 transition-transform duration-500 shadow-[0_15px_35px_-15px_rgba(26,21,20,0.5)]" data-testid="hero-img-2">
              <SmartImage src={KULHAD} alt="Kulhad chai" className="h-full w-full object-cover" />
              <span className="absolute bottom-2 left-2 font-accent text-[9px] uppercase tracking-[0.2em] bg-[#1A1514]/80 text-white px-2 py-1 rounded-sm">Kulhad Chai</span>
            </motion.div>

            {/* Small bottom image — interior */}
            <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.7, duration: 0.9, ease }}
              className="col-span-2 row-span-3 relative overflow-hidden rounded-sm -rotate-[2deg] hover:rotate-0 transition-transform duration-500 shadow-[0_15px_35px_-15px_rgba(26,21,20,0.5)]" data-testid="hero-img-3">
              <SmartImage src={INTERIOR} alt="Teaavi café interior" className="h-full w-full object-cover" />
              <span className="absolute bottom-2 left-2 font-accent text-[9px] uppercase tracking-[0.2em] bg-[#1C7C54]/95 text-white px-2 py-1 rounded-sm">Our Space</span>
            </motion.div>
          </div>

          {/* Floating stat card */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.7, ease }}
            className="absolute -bottom-4 left-4 sm:left-8 bg-[#1A1514] text-[#F5F2EB] rounded-sm px-5 py-3 flex items-center gap-4 shadow-xl">
            <div>
              <p className="font-display text-2xl leading-none">3</p>
              <p className="overline text-[#1C7C54] mt-1">Cafés · Delhi NCR</p>
            </div>
            <span className="h-8 w-px bg-white/20" />
            <div>
              <p className="font-display text-2xl leading-none">100+</p>
              <p className="overline text-[#1C7C54] mt-1">Items on Menu</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
